// Path: src/components/referral/ReferralScenarioCalculator.tsx
// Updated to enhance styling: Separated numbers (e.g., "1st") with #ffaa00 background and 🌞 icon, highlighted commission % (e.g., "5%") with #ffcc00 background for better visibility
// Added recharts BarChart to visualize earnings potential per level
// Optimized for macOS 11, mobile responsiveness (iPhone 12, 390x844), and OLS Sun Light Meditation theme
// Adjusted CommissionRate background to #ffcc00 to stand out from text (06/04/25 03:27 PM +07)
// Path: src/components/referral/ReferralScenarioCalculator.tsx
// Enhanced mobile responsiveness with touch-friendly inputs, debounced calculations, and accessible ARIA labels
// Reduced BarChart height for mobile and optimized performance
// Maintained OMS Sun Light Meditation theme with #ffaa00 accents
// 06/07/25 10:35 AM +07

import React, { useState, useEffect, useCallback } from 'react';
import { Card, InputNumber, Typography, Row, Col, Descriptions, Divider, Button, Tooltip } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { debounce } from 'lodash';

const { Title, Text } = Typography;

const CalculatorSection = styled.div`
  background: #fff5e6;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1rem 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: left;
  touch-action: pan-y;
`;

const LevelCard = styled(Card)`
  margin-bottom: 1rem;
  border: 2px solid #ffaa00;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  z-index: 1001;

  .ant-card-head {
    background: #683fa0;
    color: #ffffff;
    border-bottom: 2px solid #ffaa00;
    padding: 0.8rem;
  }

  .ant-card-body {
    padding: 1rem;
  }
`;

const LevelNumber = styled.span`
  background: #ffaa00;
  color: #ffffff;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  margin-right: 0.5rem;
  font-weight: 600;

  &:before {
    content: "🌞";
    margin-right: 0.2rem;
  }
`;

const CommissionRate = styled.span`
  background: #ffcc00;
  color: #333;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  font-weight: 700;
`;

const StyledInput = styled(InputNumber)`
  width: 100%;
  height: 44px;
  border-radius: 6px;
  border-color: #b0d9ff;
  font-size: 1rem;
  padding: 0.5rem;

  &:hover,
  &:focus {
    border-color: #ffaa00;
  }
`;

const ResetButton = styled(Button)`
  background: #683fa0;
  border-color: #ffaa00;
  color: #ffffff;
  height: 44px;
  margin-top: 1rem;
  border-radius: 6px;
  z-index: 1001;

  &:hover {
    background: #552f80;
  }
`;

const EarningsHighlight = styled.span`
  color: #ffaa00;
  font-weight: 700;
`;

interface ScenarioLevelData {
  level: number;
  commissionRate: number;
  label: string;
  referralsMadeByEachParent: number;
  avgSpendPerNewReferral: number;
}

interface CalculatedLevelInfo extends ScenarioLevelData {
  totalNewReferralsThisLevel: number;
  revenueThisLevel: number;
  earningsFromThisLevel: number;
}

const commissionRates = [0.05, 0.04, 0.03, 0.02, 0.01];

const ReferralScenarioCalculator: React.FC = () => {
  const initialLevels: ScenarioLevelData[] = [
    { level: 1, commissionRate: commissionRates[0], label: 'Level', referralsMadeByEachParent: 1, avgSpendPerNewReferral: 1000 },
    { level: 2, commissionRate: commissionRates[1], label: 'Level', referralsMadeByEachParent: 2, avgSpendPerNewReferral: 1000 },
    { level: 3, commissionRate: commissionRates[2], label: 'Level', referralsMadeByEachParent: 0, avgSpendPerNewReferral: 0 },
    { level: 4, commissionRate: commissionRates[3], label: 'Level', referralsMadeByEachParent: 0, avgSpendPerNewReferral: 0 },
    { level: 5, commissionRate: commissionRates[4], label: 'Level', referralsMadeByEachParent: 0, avgSpendPerNewReferral: 0 },
  ];

  const [levelsInput, setLevelsInput] = useState<ScenarioLevelData[]>(initialLevels);
  const [calculatedResults, setCalculatedResults] = useState<CalculatedLevelInfo[]>([]);
  const [totalPotentialEarnings, setTotalPotentialEarnings] = useState<number>(0);

  const handleInputChange = (level: number, field: keyof ScenarioLevelData, value: number | null) => {
    const numericValue = value === null ? 0 : value;
    setLevelsInput(prevLevels =>
      prevLevels.map(l =>
        l.level === level ? { ...l, [field]: numericValue } : l
      )
    );
  };

  const calculateEarnings = useCallback(() => {
    let accumulatedTotalReferralsFromPreviousLevel = 1;
    let currentTotalPotentialEarnings = 0;
    const newCalculatedResults: CalculatedLevelInfo[] = [];

    for (const currentInputLevel of levelsInput) {
      const totalNewReferralsThisLevel = accumulatedTotalReferralsFromPreviousLevel * currentInputLevel.referralsMadeByEachParent;
      const revenueThisLevel = totalNewReferralsThisLevel * currentInputLevel.avgSpendPerNewReferral;
      const earningsFromThisLevel = revenueThisLevel * currentInputLevel.commissionRate;

      newCalculatedResults.push({
        ...currentInputLevel,
        totalNewReferralsThisLevel,
        revenueThisLevel,
        earningsFromThisLevel,
      });

      currentTotalPotentialEarnings += earningsFromThisLevel;
      accumulatedTotalReferralsFromPreviousLevel = totalNewReferralsThisLevel;

      if (totalNewReferralsThisLevel === 0) {
        for (let i = currentInputLevel.level; i < levelsInput.length; i++) {
          if (!newCalculatedResults.find(r => r.level === levelsInput[i].level)) {
            newCalculatedResults.push({
              ...levelsInput[i],
              totalNewReferralsThisLevel: 0,
              revenueThisLevel: 0,
              earningsFromThisLevel: 0,
            });
          }
        }
        break;
      }
    }
    setCalculatedResults(newCalculatedResults);
    setTotalPotentialEarnings(currentTotalPotentialEarnings);
  }, [levelsInput]);

  const debouncedCalculateEarnings = useCallback(debounce(calculateEarnings, 300), [calculateEarnings]);

  const resetCalculator = () => {
    setLevelsInput(initialLevels);
  };

  useEffect(() => {
    debouncedCalculateEarnings();
    return () => debouncedCalculateEarnings.cancel();
  }, [levelsInput, debouncedCalculateEarnings]);

  const chartData = calculatedResults.map(level => ({
    name: `Level ${level.level}`,
    Earnings: level.earningsFromThisLevel,
  }));

  return (
    <CalculatorSection role="region" aria-label="Referral earnings calculator">
      <Title level={3} className="section-title" style={{ color: '#0427f4', marginTop: 0 }}>
        Visualize Your Potential Referral Earnings
      </Title>
      <Text className="description-text" style={{ marginBottom: '1rem', display: 'block', color: '#666' }}>
        Enter the number of referrals each person in your downline might make and their average spending to see your potential multi-level commission. This is an illustrative tool.
      </Text>

      {calculatedResults.map((levelData, index) => (
        <LevelCard
          key={levelData.level}
          title={
            <span>
              <LevelNumber>{levelData.level}</LevelNumber>
              {levelData.label} (Commission: <CommissionRate>{levelData.commissionRate * 100}%</CommissionRate>)
            </span>
          }
          role="group"
          aria-label={`Level ${levelData.level} inputs`}
        >
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={12} md={8}>
              <Tooltip title="Number of referrals each person from the level above (or you for Level 1) makes.">
                <Text strong style={{ color: '#0050b3' }}>Referrals per Person:</Text>
              </Tooltip>
              <StyledInput
                min={0}
                value={levelsInput[index].referralsMadeByEachParent}
                onChange={(value) => handleInputChange(levelData.level, 'referralsMadeByEachParent', value)}
                aria-label={`Number of referrals for Level ${levelData.level}`}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Tooltip title="Average spending by each new referral at this level.">
                <Text strong style={{ color: '#0050b3' }}>Avg Spend per Referral ($):</Text>
              </Tooltip>
              <StyledInput
                min={0}
                value={levelsInput[index].avgSpendPerNewReferral}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                onChange={(value) => handleInputChange(levelData.level, 'avgSpendPerNewReferral', parseFloat(value!.toString()))}
                aria-label={`Average spend for Level ${levelData.level}`}
              />
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Descriptions bordered column={1} size="small" style={{ background: '#f9f9f9' }} aria-live="polite">
                <Descriptions.Item label="Total New Referrals">
                  {levelData.totalNewReferralsThisLevel.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Potential Earnings">
                  ${levelData.earningsFromThisLevel.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </LevelCard>
      ))}
      <Divider style={{ borderColor: '#ffaa00' }} />
      <div style={{ textAlign: 'right' }} aria-live="polite">
        <Title level={4} style={{ color: '#0050b3', display: 'inline' }}>
          Total Potential Earnings (5 Levels): 
        </Title>
        <EarningsHighlight>
          ${totalPotentialEarnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </EarningsHighlight>
        <ResetButton onClick={resetCalculator} aria-label="Reset calculator">
          Reset
        </ResetButton>
      </div>
      <div style={{ marginTop: '1.5rem', height: '200px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" stroke="#333" />
            <YAxis stroke="#333" />
            <RechartsTooltip />
            <Legend />
            <Bar dataKey="Earnings" fill="#ffaa00" name="Earnings ($)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CalculatorSection>
  );
};

export default ReferralScenarioCalculator;